import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
    Button,
    Flex,
    IconButton,
    Link,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { FC, PropsWithChildren } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
    createAppointmentEditRoute,
    createAppointmentNewRoute,
    createStockDetailRoute,
} from '../../constants/routes';
import { StockOrderLabels, TaskIdentifiers } from '../../constants/stock';
import { formatDate } from '../../helpers/date';
import {
    canCreateAppointment,
    canUpdateAppointment,
    hasTask,
} from '../../helpers/stock';
import { AppointmentInterface } from '../../types/appointment';
import { StockItemInterface } from '../../types/stock';
import { ActiveStockOrderLabelType } from './StockOverview';

type Props = {
    activeOrderLabel: ActiveStockOrderLabelType;
    onOrderClick: (orderBy: ActiveStockOrderLabelType) => void;
    data: StockItemInterface[] | undefined;
    appointments: AppointmentInterface[];
};

export const StockOverviewTable: FC<Props> = ({
    activeOrderLabel,
    onOrderClick,
    data,
    appointments,
}) => {
    if (!data) {
        return <Text color={'gray.500'}>Geen voertuigen gevonden.</Text>;
    }

    return (
        <TableContainer>
            <Table variant={'striped'} size={'md'}>
                <Thead>
                    <Tr>
                        {StockOrderLabels.map((orderLabel) => (
                            <TableHeaderItem
                                key={orderLabel.title}
                                type={orderLabel.key}
                                activeLabel={activeOrderLabel}
                                onOrderClick={onOrderClick}
                                isEnabled={orderLabel.enableOrderBy}
                                isDate={orderLabel.isDate}
                            >
                                {orderLabel.title}
                            </TableHeaderItem>
                        ))}
                    </Tr>
                </Thead>

                <Tbody>
                    {data?.map((item) => (
                        <Tr
                            key={`${item.stockNumber}-${item.chassisNumber}`}
                            role="group"
                        >
                            <Td
                                textTransform={'uppercase'}
                                _groupHover={{ bgColor: 'primary.50' }}
                            >
                                <Link
                                    as={ReactRouterLink}
                                    to={createStockDetailRoute(
                                        item.stockNumber
                                    )}
                                >
                                    {item.stockNumber}
                                </Link>
                            </Td>
                            <Td _groupHover={{ bgColor: 'primary.50' }}>
                                {item.licensePlate}
                            </Td>
                            <Td
                                color={
                                    hasTask(item.type?.tasks, [
                                        TaskIdentifiers.APK,
                                    ]) && item.apkDateIsWithinTwoMonths
                                        ? 'primary.500'
                                        : undefined
                                }
                                _groupHover={{ bgColor: 'primary.50' }}
                            >
                                {item.isSuspended
                                    ? 'Geschorst'
                                    : formatDate(item.apkDate)}
                            </Td>
                            <Td
                                color={
                                    hasTask(item.type?.tasks, [
                                        TaskIdentifiers.TACHOGRAPH,
                                    ]) && item.tachographDateIsWithinTwoMonths
                                        ? 'primary.500'
                                        : undefined
                                }
                                _groupHover={{ bgColor: 'primary.50' }}
                            >
                                {formatDate(item.tachographDate)}
                            </Td>
                            <Td
                                color={
                                    hasTask(item.type?.tasks, [
                                        TaskIdentifiers.EXAMINATION,
                                    ]) && item.examinationDateIsWithinTwoMonths
                                        ? 'primary.500'
                                        : undefined
                                }
                                _groupHover={{ bgColor: 'primary.50' }}
                            >
                                {formatDate(item.examinationDate)}
                            </Td>
                            <Td
                                color={
                                    hasTask(item.type?.tasks, [
                                        TaskIdentifiers.TAILGATE,
                                    ]) && item.tailgateDateIsWithinTwoMonths
                                        ? 'primary.500'
                                        : undefined
                                }
                                _groupHover={{ bgColor: 'primary.50' }}
                            >
                                {formatDate(item.tailgateDate)}
                            </Td>
                            <Td
                                color={
                                    hasTask(item.type?.tasks, [
                                        TaskIdentifiers.CFK_EXAMINATION,
                                    ]) &&
                                    item.cfkExaminationDateDateIsWithinTwoMonths
                                        ? 'primary.500'
                                        : undefined
                                }
                                _groupHover={{ bgColor: 'primary.50' }}
                            >
                                {formatDate(item.cfkExaminationDate)}
                            </Td>
                            <Td _groupHover={{ bgColor: 'primary.50' }}>
                                {item.type?.title}
                            </Td>
                            <Td _groupHover={{ bgColor: 'primary.50' }}>
                                {item.customerNote}
                            </Td>
                            <Td _groupHover={{ bgColor: 'primary.50' }}>
                                {item.appointmentState}
                            </Td>
                            <Td _groupHover={{ bgColor: 'primary.50' }}>
                                <Flex gap={2} alignItems={'center'}>

                                    {appointments &&
                                        canCreateAppointment(
                                            appointments,
                                            item.stockNumber
                                        ) && (
                                            <Button
                                                as={ReactRouterLink}
                                                to={createAppointmentNewRoute(
                                                    item.stockNumber
                                                )}
                                                colorScheme="primary"
                                                ml={'auto'}
                                            >
                                                Afspraak maken
                                            </Button>
                                        )}

                                    {appointments &&
                                        canUpdateAppointment(
                                            appointments,
                                            item.stockNumber
                                        ) && (
                                            <Button
                                                as={ReactRouterLink}
                                                to={createAppointmentEditRoute(
                                                    item.stockNumber
                                                )}
                                                colorScheme="primary"
                                                ml={'auto'}
                                            >
                                                Afspraak aanpassen
                                            </Button>
                                        )}
                                </Flex>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    );
};

type TableHeaderProps = {
    activeLabel: ActiveStockOrderLabelType;
    type: string;
    isDate: boolean;
    onOrderClick: (orderBy: ActiveStockOrderLabelType) => void;
    isEnabled: boolean;
} & PropsWithChildren;

const TableHeaderItem: FC<TableHeaderProps> = ({
    type,
    isDate,
    activeLabel,
    onOrderClick,
    isEnabled,
    children,
}) => {
    return (
        <Th>
            <Flex alignItems={'center'} gap={2}>
                {children}

                {isEnabled && (
                    <Flex flexDir={'column'}>
                        <IconButton
                            aria-label={''}
                            icon={<ChevronUpIcon />}
                            px={2}
                            height={'auto'}
                            minW={'unset'}
                            variant={'ghost'}
                            borderRadius={0}
                            onClick={() =>
                                onOrderClick({ key: type, desc: false, isDate })
                            }
                            isActive={
                                activeLabel.key === type && !activeLabel.desc
                            }
                        />
                        <IconButton
                            aria-label={''}
                            icon={<ChevronDownIcon />}
                            px={2}
                            height={'auto'}
                            minW={'unset'}
                            variant={'ghost'}
                            borderRadius={0}
                            onClick={() =>
                                onOrderClick({ key: type, desc: true, isDate })
                            }
                            isActive={
                                activeLabel.key === type && activeLabel.desc
                            }
                        />
                    </Flex>
                )}
            </Flex>
        </Th>
    );
};
